// XrayLens — the self-aware META "X-RAY" hover tool.
//
// CONCEPT: the application is aware of itself. When the X-ray tool is ON, the
// visitor's cursor becomes an x-ray lens. Hovering reveals "the bones" (a
// wireframe schematic skeleton) and "the code" — REAL fragments read out of
// this very codebase. The headline feature is PER-WINDOW CONTEXTUAL CODE:
// hovering the lens over a specific card/window reveals THAT component's actual
// implementation, anchored directly over the card (e.g. over "sinwave.p5" you
// see SinWave's sketch, over "C:/wave-guy.p5" you see WaveGuy's box-field draw,
// over "THE EYE" you see TheEye's blink state, and so on for every window). A
// handful of generic real-code fragments fill the gaps between cards so every
// scroll depth still exposes some source.
//
// MECHANISM (so the reveal is strictly under the lens AND scrolls with the page):
//   (1) A document-anchored .xray-content layer (position:absolute, top/left 0,
//       width:100%, height = the full document height) holds ALL hidden x-ray
//       material. Because it is anchored to the DOCUMENT (not the viewport) it
//       scrolls with the page, so scrolling reveals different code. It is masked
//       by a radial-gradient circle so only a lens-sized disc is ever painted.
//       --mx/--my are DOCUMENT-space (clientX, clientY + scrollY) so the lit
//       disc stays exactly under the cursor as the page scrolls.
//   (2) Per-window code blocks are positioned by scanning the live DOM for each
//       card's title element, walking up to its card container, measuring its
//       DOCUMENT-space rect (rect + window.scrollY) and anchoring that
//       component's excerpt over it. Recomputed on scroll/resize.
//   (3) A visible lens RETICLE (position:fixed) follows the cursor at
//       (clientX, clientY): a glowing radiograph ring + crosshair + scanlines +
//       a live readout, tinted phosphor-cyan to match the CRT/VHS aesthetic.
//   (4) A body class (xray-on) lets a faint global scan tint wash the page.
//   (5) Pressing Escape disables the tool (via props.onDisable).
//
// It works over the WHOLE document (incl. the Annex), never blocks clicks
// (pointer-events:none everywhere), recomputes the layer height + anchored card
// positions on resize/scroll, updates the lit disc on BOTH mousemove and scroll
// (coalesced via rafThrottle), removes every listener on unmount, and honors
// prefers-reduced-motion (kills flicker/scanline animations, keeps the static
// reveal).
import * as React from "react";
import { useEffect, useRef, useState } from "react";
import "./xray.css";
import { rafThrottle } from "../_shared/sketchFrame";

import theeye from "../../../images/theeye.png";
import theeye2 from "../../../images/theeye2.png";
import theeye3 from "../../../images/theeye3.png";

// === PER-WINDOW CODE MAP ====================================================
// Keyed by each card's programText label (exactly as it appears in the title
// bar). Each entry is { file, code }: a short, representative excerpt of THAT
// component's real source — the sketch setup/draw or its core generating
// function — so the lens over a window reveals the window's own implementation.
const CARD_CODE = {
    // ---- main board p5 / window cards ----
    "sinwave.p5": {
        file: "p5art/SinWave.js",
        code: `// SinWave — scrolling sine ribbon, hue cycled per segment
p.setup = () => {
  p.createCanvas(700, 300);
  p.noFill();
};
p.draw = () => {
  p.background(0);
  for (let x = 0; x <= p.width + 100; x += 10) {
    wave[x / 10] = p.sin(p.radians(x + this.angle)) * 100;
  }
  for (let i = 0; i < wave.length - 1; i++) {
    p.stroke(i % 255, 255 - (i % 255), (i * 2) % 255);
    p.line(i*10, p.height/2 + wave[i], (i+1)*10, p.height/2 + wave[i+1]);
  }
  this.angle += 1;
};`,
    },
    "C:/wave-guy.p5": {
        file: "p5art/WaveGuy.js",
        code: `// WaveGuy — WEBGL grid of boxes pulsing as a radial sine wave
p.draw = () => {
  p.background(100, 100, 250);
  p.rotateX(-p.QUARTER_PI);
  p.rotateY(-r / 2);
  for (let z = 0; z < p.height; z += w) {
    for (let x = 0; x < p.width; x += w) {
      let d = p.dist(x, z, p.width/2, p.height/2);
      let a = angle + p.map(d, 0, maxD, -p.PI, p.PI);
      let h = p.floor(p.map(p.sin(a), -1, 1, 100, 300));
      p.push(); p.translate(x - p.width/2, 0, z - p.height/2);
      p.normalMaterial(); p.box(w - 2, h, w - 2); p.pop();
    }
  }
  angle -= 0.07; r += 0.01;
};`,
    },
    "pong.p5": {
        file: "p5art/PongGame.js",
        code: `// PongGame — two bots chase the ball, it ricochets off the walls
p.draw = () => {
  p.background(0); p.fill(255); p.noStroke();
  // left paddle (bot) tracks the ball
  if (this.ball.x < p.width * 0.8) {
    if (this.ball.y > this.leftPaddle.y + this.leftPaddle.height/2)
      this.leftPaddle.y += this.leftPaddle.speed;
    else this.leftPaddle.y -= this.leftPaddle.speed;
  }
  this.ball.x += this.ball.xSpeed;
  this.ball.y += this.ball.ySpeed;
};`,
    },
    "C:/color-wheel.p5": {
        file: "p5art/Kaleidoscope2D.js",
        code: `// Kaleidoscope2D — 12 colored slices rotating about the center
p.draw = () => {
  p.background(255);
  p.translate(centerX, centerY);
  for (let i = 0; i < this.numSlices; i++) {
    p.fill(colors[i]); p.beginShape();
    let a = this.rotation + (i * 360 / this.numSlices);
    let b = this.rotation + ((i + 1) * 360 / this.numSlices);
    p.vertex(0, 0);
    p.vertex(radius*p.cos(a), radius*p.sin(a));
    p.vertex(radius*p.cos(b), radius*p.sin(b));
    p.endShape(p.CLOSE);
  }
  this.rotation -= 1;
};`,
    },
    "they-are-looking.p5": {
        file: "p5art/PointingRectangles.js",
        code: `// PointingRectangles — a grid of rects that all aim at the cursor
class RotatingRectangle {
  update(targetX, targetY) {
    this.rotation = p.atan2(targetY - this.y, targetX - this.x);
  }
  display() {
    p.push();
    p.translate(this.x, this.y);
    p.rotate(this.rotation);
    p.rect(0, 0, this.width, this.height);
    p.pop();
  }
}`,
    },
    "Mr.-Waveguy": {
        file: "p5art/WaterWaves.js",
        code: `// WaterWaves — particles flow, pulled toward the cursor like fluid
update(targetX, targetY, p) {
  if (p.mouseX >= 0 && p.mouseX <= p.width) {
    const force = p.createVector(targetX - this.position.x,
                                 targetY - this.position.y);
    const distance = force.mag();
    force.normalize();
    force.mult(10 / (distance + 1));
    this.applyForce(force);
  }
  this.velocity.add(this.acceleration);
  this.velocity.limit(this.maxSpeed);
  this.position.add(this.velocity);
}`,
    },
    "timesTables.p5": {
        file: "p5art/TimesTables.js",
        code: `// TimesTables — chords drawn across a ring of points (modular times)
while (angle <= 360) {
  let hue = p.map(p.sin(p.radians(angle * times)), -1, 1, 0, 255);
  p.stroke(hue, 255, p.map(p.cos(p.radians(angle)), -1, 1, 50, 255));
  let x = p.cos(p.radians(angle)) * radius;
  let y = p.sin(p.radians(angle)) * radius;
  p.point(x, y);
  let num = p.floor(index * times);
  angle += angleInc; index++;
}`,
    },
    "wavegen.p5": {
        file: "p5art/SineWaveGraph.js",
        code: `// SineWaveGraph — vertical bars sampling a marching sine wave
p.draw = () => {
  p.background(0); p.noFill(); p.stroke(255);
  for (let x = 0; x < p.width; x += 10) {
    let y = p.sin(x * this.frequency + this.phase)
            * this.amplitude + p.height / 2;
    p.rect(x, p.height / 2, 10, y - p.height / 2);
  }
  this.phase += 0.03; // march the wave sideways
};`,
    },
    "bigsmall.p5": {
        file: "p5art/InteractiveArt.js",
        code: `// InteractiveArt — one ellipse whose size+hue track the cursor
p.draw = () => {
  let mx = p.mouseX / p.width;
  let my = p.mouseY / p.height;
  let size = p.map(p.dist(p.mouseX, p.mouseY, p.width/2, p.height/2),
                   0, p.width/2, 20, 300);
  p.background(360 * mx, 100, 100);
  p.noStroke();
  p.fill(p.color(360 * mx, 100 * my, 100));
  p.ellipse(p.mouseX, p.mouseY, size, size);
};`,
    },
    "hover-square.p5": {
        file: "p5art/InteractiveDrawing.js",
        code: `// InteractiveDrawing — stamps squares/ellipses along the cursor
p.draw = () => {
  let r = p.map(p.mouseX, 0, p.width, 0, 255);
  let g = p.map(p.mouseY, 0, p.height, 0, 255);
  p.background(backgroundColor);
  p.fill(r, g, b); p.noStroke();
  if (p.random(0, 3) == 0) p.ellipse(p.mouseX, p.mouseY, 50, 50);
  else p.rect(p.mouseX - 25, p.mouseY - 25, 50, 50);
};
p.mousePressed = () => { backgroundColor = 255 - backgroundColor; };`,
    },
    "hover-flow.p5": {
        file: "p5art/FlowArt.js",
        code: `// FlowArt — moving the mouse spawns fading HSB particles
p.mouseMoved = () => {
  let hue = p.random(0, 360);
  let particle = new Particle(p.mouseX, p.mouseY,
    hue, p.random(50,100), p.random(50,100), p.random(10,25), p);
  this.particles.push(particle);
};
p.draw = () => {
  p.background(0);
  for (let i = this.particles.length - 1; i >= 0; i--) {
    this.particles[i].update();
    this.particles[i].display(p);
    if (this.particles[i].isDead()) this.particles.splice(i, 1);
  }
};`,
    },
    "terrain-generation.p5": {
        file: "p5art/PerlinNoise2D.js",
        code: `// PerlinNoise2D — a WEBGL terrain mesh scrolling through noise()
let yoff = this.n;
for (let y = 0; y < this.rows; y++) {
  let xoff = 0;
  for (let x = 0; x < this.cols; x++) {
    this.terrain[x][y] = p.map(p.noise(xoff, yoff), 0, 1, -100, 100);
    xoff += this.inc;
  }
  yoff += this.inc;
}
this.n -= 0.009; // scroll the noise field toward the viewer`,
    },
    "hoolahoop.p5": {
        file: "p5art/SphereAnimation.js",
        code: `// SphereAnimation — a swirling ring of tiny hue-cycled spheres
p.rotateX(this.angle); p.rotateY(this.angle);
for (let i = 0; i < numParticles; i++) {
  let x = radius * p.sin(i * p.TWO_PI / numParticles);
  let y = radius * p.cos(i * p.TWO_PI / numParticles);
  p.stroke((i + p.frameCount) % 360, 255, 255);
  p.push(); p.translate(x, y);
  let swirl = p.sin(this.angle * 5 + i * 0.2) * 50;
  p.rotateX(p.radians(swirl)); p.rotateY(p.radians(swirl));
  p.sphere(particleSize); p.pop();
}
this.angle += 0.01;`,
    },
    "tangent-spaceinvaders.p5": {
        file: "p5art/TriangleWaveAnimation.js",
        code: `// TriangleWaveAnimation — triangles riding a tan() wave (so it leaps)
p.draw = () => {
  p.background(0); p.noFill(); p.stroke(255);
  for (let x = 0; x < p.width + this.triangleSize; x += this.triangleSize) {
    let y = -p.tan(x * this.frequency + this.phase)
            * this.amplitude + p.height / 2;
    this.drawEquilateralTriangle(x, y, this.triangleSize);
  }
  this.phase += 0.05;
};`,
    },
    "solar-system.p5": {
        file: "p5art/SolarSystem3D.js",
        code: `// SolarSystem3D — orbiting planets + moons in a WEBGL camera
this.planets = [
  { name: 'Sun',     radius: 100, orbitRadius: 0,   speed: 0    },
  { name: 'Mercury', radius: 10,  orbitRadius: 150, speed: 1    },
  { name: 'Venus',   radius: 15,  orbitRadius: 250, speed: 0.5  },
  { name: 'Earth',   radius: 20,  orbitRadius: 350, speed: 0.25,
    moons: [{ name: 'Moon', radius: 5, orbitRadius: 50, speed: 1 }] },
];
p.camera(0, -800, 800, 0, 0, 0, 0, 1, 0);`,
    },
    "X:/marquee.css": {
        file: "MarqueeCard/MarqueeCard.js",
        code: `// MarqueeCard — alternating LEFT/RIGHT scrolling bands
<div className="marquee-wrap">
  <div className="marquee-inner">
    <span className="marquee-left">{"<-LEFT<-LEFT<-LEFT"}</span>
  </div>
  <div className="marquee-inner">
    <span className="marquee-right">{"RIGHT->RIGHT->RIGHT->"}</span>
  </div>
</div>`,
    },

    // ---- Annex windows ----
    "AMBULATE.EXE": {
        file: "CrtTerminal/CrtTerminal.js",
        code: `// CrtTerminal — green-phosphor DOS shell w/ a walking manifesto
const MANIFESTO = [
  "AMBULATE.EXE // a found-object ideaboard that walks at night.",
  "we do not browse. we ambulate. one foot, then the wet other.",
  "every pixel here is a small eye that learned to blink.",
];
const BOOT = [
  "AMBULATE-DOS  v1.97  [phosphor build]",
  "(c) 1997-2026 ambulation zone. all eyes reserved.",
  "type help and walk.",
];`,
    },
    "ambulation-field.p5": {
        file: "AmbulationField/AmbulationField.js",
        code: `// AmbulationField — 80 walkers on a noise flow-field; they
// periodically coalesce into a watching eye, then disperse.
p.draw = () => {
  p.noStroke(); p.fill(12, 8, 6, 18);
  p.rect(0, 0, 450, 350); // translucent rust/sepia trail fade
  const gazing = (this.t % 600) > 480;
  for (const wk of this.walkers) {
    let ang = p.noise(wk.pos.x*0.004, wk.pos.y*0.004, zoff) * p.TWO_PI*2;
    wk.vel.add(p.createVector(p.cos(ang), p.sin(ang)));
  }
};`,
    },
    "THE EYE": {
        file: "TheEye/TheEye.js",
        code: `// TheEye — a believable eye that follows you and blinks
this.blink = 0;       // 0..1 eyelid closure (eased)
this.nextBlink = 90;  // frame the next blink begins
this.dilateTarget = 0.4;
// iris+pupil ease toward the cursor, dart in tiny saccades when idle
this.pupil.x += (this.target.x - this.pupil.x) * 0.08;
this.pupil.y += (this.target.y - this.pupil.y) * 0.08;
if (this.frameCount >= this.nextBlink) this.blinking = true;`,
    },
    "SPRAY.EXE": {
        file: "SprayCanvas/SprayCanvas.js",
        code: `// SprayCanvas — drag to spray grainy paint with drips on brick
const COLORS = ["#ff1900","#12fe8d","#75f8ff","#ed77ff","#fefa12"];
function getPoint(e) {
  const rect = canvasRef.current.getBoundingClientRect();
  return { x: e.clientX - rect.left, y: e.clientY - rect.top };
}
// Clear wipes the wall + stamps a bullethole where you last sprayed.`,
    },
    "GUESTBOOK.RCP": {
        file: "ReceiptGuestbook/ReceiptGuestbook.js",
        code: `// ReceiptGuestbook — prints a thermal RECEIPT OF YOUR VISIT
const FORTUNES = [
  "you have been walking in circles",
  "the eye approves",
  "your gait is irregular and beautiful",
  "diagnosis: chronic almost",
];
function randomFortune() {
  return FORTUNES[Math.floor(Math.random() * FORTUNES.length)];
}
// entries persist in localStorage, stack as torn receipt slips.`,
    },
    "TAPE DECK": {
        file: "CassetteDeck/CassetteDeck.js",
        code: `// CassetteDeck — brushed-metal deck; reels spin only while playing
const VU_COLORS = [
  "#12fe8d","#12fe8d","#12fe8d","#12fe8d",
  "#ffa144","#ffa144","#ffa144",
  "#ff1900","#ff1900","#ff1900",
];
function fmt(sec) {
  const s = Math.max(0, Math.floor(sec || 0));
  return pad(Math.floor(s / 60)) + ":" + pad(s % 60);
}`,
    },
    "WATCHERS.EXE": {
        file: "EyeWall/EyeWall.js",
        code: `// EyeWall — a panel of many SVG eyes, all pupils tracking the cursor
const TRAVEL_X = 11; // max horizontal iris travel (svg units)
const TRAVEL_Y = 7;  // max vertical iris travel (svg units)
// ONE shared rafThrottle'd mousemove translates every iris toward
// the pointer, clamped inside the sclera. Blinks are CSS-only.
const dx = clamp((mx - cx) / cx, -1, 1) * TRAVEL_X;
const dy = clamp((my - cy) / cy, -1, 1) * TRAVEL_Y;`,
    },
    "PHARMACY.RCP": {
        file: "Prescription/Prescription.js",
        code: `// Prescription — AMBULATE PHARMACY prints a surreal Rx slip
function makeRx() {
  const drug = pick(data.drugs);
  const form = pick(data.forms);
  const dose = pick(data.doses);
  const refills = Math.floor(Math.random() * 100);
  return { drug, form, dose, refills, sig: pick(data.sigs),
           dx: pick(data.diagnoses), serial: pad(Math.random()*1e9, 9) };
}`,
    },
    "DEAD-CHANNEL": {
        file: "DeadChannel/DeadChannel.js",
        code: `// DeadChannel — a haunted CRT TV; a knob cycles the playlist
//  1) snow/static  2-3) feeds behind scanlines
//  4) SMPTE NO-SIGNAL bars  5) a slow text crawl
const SMPTE_BARS = [
  "#c0c0c0","#c0c000","#00c0c0","#00c000",
  "#c000c0","#c00000","#0000c0",
];
// static "snow" is a cheap CSS keyframe — no per-pixel canvas loop.`,
    },
};

// CSS selectors for the three card title elements, paired with how far up the
// DOM to walk to reach the draggable card container that wraps the whole window.
const TITLE_SELECTORS = [
    ".skills-text-title",   // SkillCard
    ".w-program-title",     // WindowsCard
    ".m-program-title",     // MacCard
];

// A small, hand-laid schematic skeleton — "the bones" that hold up the structure.
// Pure SVG: structural lines, rivet/bolt callouts, dashed measurement guides,
// and a few circuit traces. Stroked with currentColor so the .xray-content
// phosphor tint flows straight through.
function BoneSchematic() {
    return (
        <svg
            className="xr-bones"
            viewBox="0 0 1440 900"
            preserveAspectRatio="xMidYMid slice"
            aria-hidden="true"
        >
            {/* structural frame */}
            <rect x="40" y="40" width="1360" height="820" className="xr-stroke xr-dim" rx="4" />
            <rect x="80" y="80" width="1280" height="740" className="xr-stroke xr-dashed" rx="2" />

            {/* spine + ribs running down the middle */}
            <line x1="720" y1="90" x2="720" y2="810" className="xr-stroke" />
            <line x1="620" y1="190" x2="820" y2="190" className="xr-stroke xr-dim" />
            <line x1="600" y1="280" x2="840" y2="280" className="xr-stroke xr-dim" />
            <line x1="610" y1="370" x2="830" y2="370" className="xr-stroke xr-dim" />
            <line x1="600" y1="460" x2="840" y2="460" className="xr-stroke xr-dim" />
            <line x1="620" y1="550" x2="820" y2="550" className="xr-stroke xr-dim" />

            {/* circuit traces fanning out left */}
            <polyline points="80,200 240,200 240,320 360,320" className="xr-stroke xr-trace" />
            <polyline points="80,420 200,420 200,520 320,520 320,620" className="xr-stroke xr-trace" />
            <polyline points="80,640 180,640 180,720 300,720" className="xr-stroke xr-trace" />
            {/* circuit traces fanning out right */}
            <polyline points="1360,240 1180,240 1180,360 1040,360" className="xr-stroke xr-trace" />
            <polyline points="1360,500 1220,500 1220,600 1080,600" className="xr-stroke xr-trace" />

            {/* bolt / rivet callouts */}
            {[
                [240, 200], [360, 320], [320, 620], [300, 720],
                [1180, 240], [1040, 360], [1080, 600],
                [80, 80], [1360, 80], [80, 820], [1360, 820],
            ].map(([cx, cy], i) => (
                <g key={i} className="xr-bolt">
                    <circle cx={cx} cy={cy} r="9" className="xr-stroke" />
                    <circle cx={cx} cy={cy} r="3" className="xr-fill" />
                    <line x1={cx - 14} y1={cy} x2={cx + 14} y2={cy} className="xr-stroke xr-hair" />
                    <line x1={cx} y1={cy - 14} x2={cx} y2={cy + 14} className="xr-stroke xr-hair" />
                </g>
            ))}

            {/* dashed measurement guides w/ end ticks */}
            <g className="xr-measure">
                <line x1="80" y1="120" x2="1360" y2="120" className="xr-stroke xr-dashed" />
                <line x1="80" y1="110" x2="80" y2="130" className="xr-stroke" />
                <line x1="1360" y1="110" x2="1360" y2="130" className="xr-stroke" />
                <text x="700" y="112" className="xr-svg-label">1280.0u</text>

                <line x1="120" y1="160" x2="120" y2="780" className="xr-stroke xr-dashed" />
                <line x1="110" y1="160" x2="130" y2="160" className="xr-stroke" />
                <line x1="110" y1="780" x2="130" y2="780" className="xr-stroke" />
                <text x="64" y="470" className="xr-svg-label" transform="rotate(-90 64 470)">620.0u</text>
            </g>

            {/* skeletal hand bottom-right — schematic finger bones */}
            <g className="xr-hand">
                {[0, 1, 2, 3, 4].map((f) => {
                    const x = 1120 + f * 46;
                    return (
                        <g key={f}>
                            <line x1={x} y1="700" x2={x} y2={560 - f * 8} className="xr-stroke" />
                            <circle cx={x} cy={560 - f * 8} r="5" className="xr-stroke" />
                            <circle cx={x} cy={630} r="4" className="xr-stroke" />
                        </g>
                    );
                })}
                <path d="M1118 700 Q1210 760 1306 700" className="xr-stroke" />
            </g>
        </svg>
    );
}

// "the code" — a REAL excerpt of the app's own source, in dot/mono font, with a
// little filename tag so you can see which file the lens just cut into. Supports
// either percentage anchoring (top/left/right) or absolute px anchoring (for the
// per-window blocks measured from the live DOM).
function CodeFragment({ file, top, left, right, width, children }) {
    const style = { top };
    if (left != null) style.left = left;
    if (right != null) style.right = right;
    if (width != null) style.maxWidth = width;
    return (
        <div className="xr-code-block" style={style} aria-hidden="true">
            {file ? <span className="xr-code-file">{file}</span> : null}
            <pre className="xr-code">{children}</pre>
        </div>
    );
}

// self-aware SECRET messages in the application's own voice (kept rare).
function Secret({ children, className, style }) {
    return (
        <div className={"xr-secret " + (className || "")} style={style} aria-hidden="true">
            {children}
        </div>
    );
}

// === REAL CODE, READ OUT OF THIS CODEBASE ==================================
// The PER-WINDOW excerpts above (CARD_CODE) are the point: the lens over a card
// reveals that card's own source. The generic fragments below fill the gaps
// BETWEEN cards (shared utilities, the audio engines, this lens itself) so every
// scroll depth still exposes some real source. Each is an actual excerpt from
// the file named in its tag, positioned down the WHOLE document (top is a % of
// the document height).

function XrayLens(props) {
    const active = props.active === true;
    const onDisable = props.onDisable;

    // The masked overlay + the reticle. We poke their CSS vars directly from the
    // rAF-throttled handler to avoid a React re-render per mousemove/scroll.
    const contentRef = useRef(null);
    const reticleRef = useRef(null);
    // Live coordinate readout text node (updated imperatively, same reason).
    const coordRef = useRef(null);
    // Last known cursor in viewport space, so a scroll event (which has no
    // clientX/clientY) can recompute the document-space lens centre.
    const cursorRef = useRef({ x: -9999, y: -9999 });

    // Per-window code blocks anchored over the live cards. Each entry is
    // { key, file, code, top, left, width } in DOCUMENT space. Recomputed on
    // mount, scroll and resize so each block stays glued to its card.
    const [cardBlocks, setCardBlocks] = useState([]);
    // Keep onDisable reachable from the keydown handler without re-binding it.
    const onDisableRef = useRef(onDisable);
    useEffect(() => { onDisableRef.current = onDisable; }, [onDisable]);

    useEffect(() => {
        if (!active) return;
        if (typeof window === "undefined") return;

        const content = contentRef.current;
        const reticle = reticleRef.current;

        // Wash the whole page with a faint scan tint while scanning.
        document.body.classList.add("xray-on");

        // The hidden layer is anchored to the DOCUMENT, so it must be as tall as
        // the whole document. Measure + restate on resize (content reflow can
        // change the document height).
        const sizeLayer = () => {
            if (!content) return;
            const h = Math.max(
                document.documentElement.scrollHeight,
                document.body ? document.body.scrollHeight : 0
            );
            content.style.height = h + "px";
        };
        sizeLayer();

        // Scan the live DOM for every card title, read its programText label,
        // walk up to the card container, and measure its DOCUMENT-space rect so
        // its excerpt can be anchored directly over it. Only labels present in
        // CARD_CODE produce a block; everything else is left to the generic fill.
        const measureCards = () => {
            const out = [];
            const seen = new Set();
            for (const sel of TITLE_SELECTORS) {
                const titles = document.querySelectorAll(sel);
                for (const titleEl of titles) {
                    const label = (titleEl.textContent || "").trim();
                    const entry = CARD_CODE[label];
                    if (!entry) continue;
                    // The card container is the nearest positioned ancestor that
                    // wraps the whole window (the draggable card div). Closest
                    // handle/title bar -> its parent card. Fall back gracefully.
                    const card =
                        titleEl.closest(
                            ".box-shadow, .multi-array-window, .windows-programface, .mac-programface"
                        ) || titleEl.parentElement;
                    const target = card || titleEl;
                    const rect = target.getBoundingClientRect();
                    if (rect.width === 0 && rect.height === 0) continue;
                    // de-dupe if the same card matched twice somehow
                    const key = label + "@" + Math.round(rect.left) + "," +
                        Math.round(rect.top + window.scrollY);
                    if (seen.has(key)) continue;
                    seen.add(key);
                    out.push({
                        key,
                        file: entry.file,
                        code: entry.code,
                        top: Math.round(rect.top + window.scrollY + 6),
                        left: Math.round(rect.left + window.scrollX + 6),
                        width: Math.max(220, Math.round(rect.width - 12)),
                    });
                }
            }
            setCardBlocks(out);
        };
        measureCards();

        // Write the lit-disc centre. The reticle is fixed → viewport coords; the
        // content layer scrolls with the document → DOCUMENT coords (+scrollY).
        const paint = () => {
            const { x, y } = cursorRef.current;
            if (reticle) {
                reticle.style.setProperty("--mx", x + "px");
                reticle.style.setProperty("--my", y + "px");
            }
            if (content) {
                content.style.setProperty("--mx", x + "px");
                content.style.setProperty("--my", (y + window.scrollY) + "px");
            }
            if (coordRef.current) {
                coordRef.current.textContent =
                    String(Math.round(x)).padStart(4, "0") +
                    " , " +
                    String(Math.round(y + window.scrollY)).padStart(4, "0");
            }
        };

        // Coalesce the (potentially very noisy) mousemove + scroll to one write
        // per frame via the shared rafThrottle helper.
        const onFrame = rafThrottle(paint);
        // Re-measuring the cards is heavier (layout reads), so throttle it on its
        // own frame too; it must follow scroll so anchors track the page.
        const onMeasureFrame = rafThrottle(measureCards);

        const onMove = (e) => {
            cursorRef.current = { x: e.clientX, y: e.clientY };
            onFrame();
        };
        // Scroll carries no pointer position, but the lit disc must follow the
        // page: reuse the last cursor and recompute its document-space Y. The
        // anchored card blocks themselves are in document space and scroll with
        // the layer, but a re-measure keeps them honest after reflow.
        const onScroll = () => {
            onFrame();
            onMeasureFrame();
        };
        const onResize = () => {
            sizeLayer();
            onFrame();
            onMeasureFrame();
        };

        // Esc disables the X-ray tool.
        const onKeyDown = (e) => {
            if (e.key === "Escape" || e.key === "Esc") {
                if (typeof onDisableRef.current === "function") onDisableRef.current();
            }
        };

        window.addEventListener("mousemove", onMove, { passive: true });
        window.addEventListener("scroll", onScroll, { passive: true });
        window.addEventListener("resize", onResize, { passive: true });
        window.addEventListener("keydown", onKeyDown);

        return () => {
            window.removeEventListener("mousemove", onMove);
            window.removeEventListener("scroll", onScroll);
            window.removeEventListener("resize", onResize);
            window.removeEventListener("keydown", onKeyDown);
            if (typeof onFrame.cancel === "function") onFrame.cancel();
            if (typeof onMeasureFrame.cancel === "function") onMeasureFrame.cancel();
            document.body.classList.remove("xray-on");
        };
    }, [active]);

    if (!active) return null;

    return (
        <React.Fragment>
            {/* (1) The document-anchored masked layer holding ALL hidden x-ray
                 material. Starts off-screen (--mx/--my at -9999px) so nothing
                 shows until the first move. */}
            <div className="xray-content" ref={contentRef} aria-hidden="true">
                {/* (a) the bones */}
                <BoneSchematic />

                {/* (b) PER-WINDOW CODE — each card's own source, anchored over
                     the card itself (measured from the live DOM in document
                     space). This is the headline reveal: the lens over a window
                     shows that window's implementation. */}
                {cardBlocks.map((b) => (
                    <CodeFragment
                        key={b.key}
                        file={b.file}
                        top={b.top + "px"}
                        left={b.left + "px"}
                        width={b.width + "px"}
                    >
                        {b.code}
                    </CodeFragment>
                ))}

                {/* (c) generic fill — REAL excerpts of shared code between the
                     cards, so every scroll depth still exposes some source */}

                {/* --- App.js (the toggles, the actual JSX wiring) --- */}
                <CodeFragment file="pages/App.js" top="3%" left="5%">
{`const [soundEnabled, setSoundEnabled] = useState(true);
const [annexEnabled, setAnnexEnabled] = useState(false);
const [xrayEnabled, setXrayEnabled] = useState(false);
const [tonesEnabled, setTonesEnabled] = useState(true);`}
                </CodeFragment>

                <CodeFragment file="pages/App.js" top="6%" right="5%">
{`<SystemCrash onUnlock={() => setAnnexEnabled(true)} />
<XrayLens active={xrayEnabled} onDisable={() => setXrayEnabled(false)} />`}
                </CodeFragment>

                {/* --- sketchFrame.js (the very helper this lens uses) --- */}
                <CodeFragment file="_shared/sketchFrame.js" top="15%" right="6%">
{`export function pauseWhenHidden(p5Instance, element, opts = {}) {
  if (!p5Instance || !element) return noop;
  return whenVisible(
    element,
    () => { p5Instance.loop(); },
    () => { p5Instance.noLoop(); },
    opts
  );
}`}
                </CodeFragment>

                <CodeFragment file="_shared/sketchFrame.js" top="20%" left="6%">
{`export function rafThrottle(fn) {
  let scheduled = false;
  const throttled = function (...args) {
    lastArgs = args;
    if (scheduled) return;
    scheduled = true;
    handle = raf(() => { scheduled = false; fn.apply(lastThis, lastArgs); });
  };
  return throttled;
}`}
                </CodeFragment>

                {/* --- toneEngine.js (the shared synth) --- */}
                <CodeFragment file="_shared/toneEngine.js" top="30%" left="7%">
{`function ensureContext() {
  if (!ctx) {
    ctx = new AudioCtx();
    master = ctx.createGain();
    master.gain.value = 0.9;
    master.connect(ctx.destination);
  }
  return ctx;
}`}
                </CodeFragment>

                {/* --- fmAudio.js (the Static FM engine) --- */}
                <CodeFragment file="StaticFM/fmAudio.js" top="46%" right="7%">
{`const STATION_ROOTS = [110.0, 130.81, 146.83, 164.81, 196.0];

setTuning(idx, staticAmount) {
  if (idx !== this.tunedIdx) {
    this.tunedIdx = idx;
    this._buildOscsFor(idx);
    this._buildVoiceFor(idx);
    this._buildMelodyFor(idx);
  }
}`}
                </CodeFragment>

                {/* --- SystemCrash.js (the scroll-triggered kernel panic) --- */}
                <CodeFragment file="SystemCrash/SystemCrash.js" top="58%" right="6%">
{`checkScroll() {
  const doc = document.documentElement;
  const full = Math.max(doc.scrollHeight, document.body.scrollHeight);
  const scrollable = full - window.innerHeight;
  const progress = scrollable > 0 ? window.scrollY / scrollable : 1;
  if (progress >= 0.45) this.crash();
}`}
                </CodeFragment>

                {/* --- Annex.js (the second wing the crash unlocks) --- */}
                <CodeFragment file="Annex/Annex.js" top="70%" left="7%">
{`function returnToSurface() {
  var reduce = window.matchMedia &&
    window.matchMedia("(prefers-reduced-motion: reduce)").matches;
  window.scrollTo({ top: 0, behavior: reduce ? "auto" : "smooth" });
}`}
                </CodeFragment>

                {/* --- XrayLens.js (this very file, reading itself) --- */}
                <CodeFragment file="Xray/XrayLens.js" top="81%" right="6%">
{`// the content layer scrolls with the document → DOCUMENT coords:
content.style.setProperty("--my", (y + window.scrollY) + "px");
// the reticle is fixed → viewport coords:
reticle.style.setProperty("--my", y + "px");`}
                </CodeFragment>

                <CodeFragment file="Xray/xray.css" top="87%" left="6%">
{`mask-image: radial-gradient(
  circle var(--xr, 130px) at var(--mx) var(--my),
  #000 0 62%, transparent 100%
);`}
                </CodeFragment>

                {/* (d) only a few self-aware whispers — the code is the point now */}
                <Secret className="xr-big" style={{ top: "8%", left: "40%" }}>
                    you are reading my source.
                </Secret>
                <Secret className="xr-glow" style={{ top: "44%", left: "42%" }}>
                    hello, debugger.
                </Secret>
                <Secret style={{ top: "67%", left: "40%" }}>
                    the bones hold up the structure.
                </Secret>
                <Secret className="xr-sig" style={{ top: "97%", right: "5%" }}>
                    — signed, the zone // 31.78°N 35.21°E
                </Secret>

                {/* (e) the weird EYE PNGs peering out from beneath the source —
                     scattered at different document depths (so different scroll
                     positions uncover different eyes), varied sizes + slight
                     rotations, phosphor-tinted by .xr-eye so they read as ghostly
                     cyan radiograph eyes rather than full-color. */}
                <img className="xr-eye" src={theeye} alt="" style={{ top: "11%", left: "12%", width: "96px", "--xr-eye-rot": "-7deg" }} />
                <img className="xr-eye" src={theeye3} alt="" style={{ top: "27%", left: "60%", width: "118px", "--xr-eye-rot": "5deg" }} />
                <img className="xr-eye" src={theeye2} alt="" style={{ top: "41%", right: "10%", width: "84px", "--xr-eye-rot": "-11deg" }} />
                <img className="xr-eye" src={theeye} alt="" style={{ top: "62%", left: "55%", width: "92px", "--xr-eye-rot": "9deg" }} />
                <img className="xr-eye" src={theeye3} alt="" style={{ top: "76%", left: "20%", width: "70px", "--xr-eye-rot": "-4deg" }} />
                <img className="xr-eye" src={theeye2} alt="" style={{ top: "90%", left: "66%", width: "104px", "--xr-eye-rot": "13deg" }} />
            </div>

            {/* (2) The visible lens reticle — glowing ring + crosshair +
                 scanlines + live readout. Fixed to the viewport, never
                 intercepts clicks. */}
            <div className="xray-reticle" ref={reticleRef} aria-hidden="true">
                <div className="xr-ring" />
                <div className="xr-ring xr-ring-inner" />
                <div className="xr-scan" />
                <div className="xr-cross xr-cross-h" />
                <div className="xr-cross xr-cross-v" />
                <div className="xr-readout">
                    <span className="xr-readout-title">X-RAY // SCANNING</span>
                    <span className="xr-readout-coord" ref={coordRef}>0000 , 0000</span>
                    <span className="xr-readout-hint">ESC TO CLOSE</span>
                </div>
            </div>
        </React.Fragment>
    );
}

export default XrayLens;
